import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import IconFrame from '@/components/atom/IconFrame';
import ContentFile from '@/components/atom/icon/ContentFile';
import Landscape from '@/components/atom/icon/Landscape';
import MailChatBubbleTypingOval from '@/components/atom/icon/MailChatBubbleTypingOval';
import QnaReport from '@/components/atom/icon/Report/QnaReport';
import UserBlock from '@/components/atom/icon/Report/UserBlock';
import UserReport from '@/components/atom/icon/Report/UserReport';
import UserBlockRemove from '@/components/atom/icon/UserBlockRemove';
import { SelectModalListItem } from '@/components/molecules/Modal/SelectModal';
import { REPORT_TYPE } from '@/components/templates/mobile/communication/Common/ReportTemplate';
import theme from '@/styles/theme';
import { AttachmentBlindType } from '@/types/bff/gql-schema';
import { ROUTES } from '@/types/common';
import { MODAL_BUTTON_TYPE, MODAL_TYPE } from '@/types/modal';
import { historyBack } from '@/utils/mobile/RNPlugins';

import {
  useBlindBoardCommentMutation,
  useBlindBoardPostAttachmentMutation,
  useBlindBoardPostMutation,
  useDeleteBoardCommentMutation,
  useDeleteBoardPostMutation,
  useUpdateBlindBoardCommentOnlyMutation,
  useUpdateBlindBoardPostOnlyMutation,
} from './community/mobile/community.gql.impl';
import useCustomRouter from './useCustomRouter';
import useModal from './useModal';
import useScreenFocus from './useScreenFocus';
import useToast from './useToast';

export interface PostReportDataType {
  targetType: REPORT_TYPE;
  targetId: string;
  isMine: boolean;
  isDetailPage?: boolean;
  isAdmin?: boolean;
  attachmentTargetId?: string;
}

const usePostReportModal = () => {
  const focused = useScreenFocus();
  const queryClient = useQueryClient();
  const { moveToPage } = useCustomRouter();
  const { showToast } = useToast();
  const { showModal, closeModal } = useModal();
  const [showReportModal, setShowReportModal] = useState<boolean>(false);
  const [reportData, setReportData] = useState<PostReportDataType>();
  const [reportItems, setReportItems] = useState<Array<SelectModalListItem>>();

  const isPost = reportData?.targetType === REPORT_TYPE.BOARD_POST;
  const reportType = isPost ? '게시글' : '댓글';

  const { mutate: deleteBoardPost } = useDeleteBoardPostMutation();
  const { mutate: deleteBoardComment } = useDeleteBoardCommentMutation();

  // Admin Blind post, comment, postAttachment
  const { mutate: blindBoardPost } = useBlindBoardPostMutation();
  const { mutate: blindBoardComment } = useBlindBoardCommentMutation();
  const { mutate: blindBoardPostAttachment } = useBlindBoardPostAttachmentMutation();
  const { mutate: onUpdateBlindBoardPostOnly } = useUpdateBlindBoardPostOnlyMutation();
  const { mutate: onUpdateBlindBoardCommentOnly } = useUpdateBlindBoardCommentOnlyMutation();

  const onCloseReportModal = () => {
    setShowReportModal(false);
    setReportData(undefined);
    setReportItems([]);
  };

  const onShowReportModal = (data: PostReportDataType) => {
    setReportData(data);
  };

  const onReportPost = (isReportUser?: boolean) => {
    if (reportData) {
      setShowReportModal(false);

      const report_type = isReportUser ? 'user' : reportData?.targetType;
      moveToPage({
        webLink: `${ROUTES.COMMON.REPORT.path}?targetId=${reportData?.targetId}&type=${reportData?.targetType}&report_type=${report_type}`,
        mobileScreenName: ROUTES.COMMON.REPORT.appScreenName,
        params: {
          targetId: reportData.targetId,
          type: reportData.targetType,
          report_type: report_type,
        },
      });
    }
  };

  const onError = () => {
    showToast({ message: '요청에 실패했습니다.' });
    onCloseReportModal();
  };

  const onSuccess = () => {
    showToast({ message: `${reportType}을 삭제했어요` });
    if (reportData?.isDetailPage && isPost) {
      setTimeout(() => historyBack(), 1600);
    }
    queryClient.invalidateQueries();
    closeModal();
  };

  const onDeletePost = () => {
    if (reportData) {
      deleteBoardPost({ boardPostPid: reportData.targetId }, { onError, onSuccess });
    }
  };

  const onDeleteComment = () => {
    if (reportData) {
      deleteBoardComment({ boardCommentPid: reportData.targetId }, { onError, onSuccess });
    }
  };

  const onSuccessMutationByAdmin = ({
    type,
    count,
    days,
  }: {
    type: string;
    count?: number;
    days?: string;
  }) => {
    closeModal();
    if (reportData?.isDetailPage && type === 'blindPost') {
      setTimeout(() => historyBack(), 1600);
    }
    switch (type) {
      case 'blindPost':
      case 'blindComment': {
        const formattedType = type === 'blindPost' ? '게시글' : '댓글';
        return showToast({
          message: `해당 ${formattedType} 삭제와 사용자의 커뮤니티 이용을 제한 했어요. (누적 ${count}회 ${days})`,
        });
      }
      case 'blindPostAttachment': {
        return showToast({ message: '이미지를 숨김 처리했어요.' });
      }
    }

    queryClient.invalidateQueries();
  };

  const onSuccessMutationOnlyBlindByAdmin = () => {
    closeModal();
    if (isPost) {
      setTimeout(() => historyBack(), 1600);
    }
    showToast({
      message: `해당 ${isPost ? '게시글' : '댓글'}을 삭제했어요.`,
    });
    queryClient.invalidateQueries();
  };

  const onDeletePostAndBannedByAdmin = () => {
    if (reportData) {
      blindBoardPost(
        { boardPostPid: reportData.targetId },
        {
          onError,
          onSuccess: (data) =>
            onSuccessMutationByAdmin({
              type: 'blindPost',
              count: data.blindBoardPost.userBlindedCount,
              days: data.blindBoardPost.userBannedDayCountMessage,
            }),
        }
      );
    }
  };

  const onDeleteCommentAndBannedByAdmin = () => {
    if (reportData) {
      blindBoardComment(
        { boardCommentPid: reportData.targetId },
        {
          onError,
          onSuccess: (data) =>
            onSuccessMutationByAdmin({
              type: 'blindComment',
              count: data.blindBoardComment.userBlindedCount,
              days: data.blindBoardComment.userBannedDayCountMessage,
            }),
        }
      );
    }
  };

  const onDeleteOnlyPostByAdmin = () => {
    if (reportData) {
      onUpdateBlindBoardPostOnly(
        { boardPostPid: reportData.targetId },
        {
          onError,
          onSuccess: onSuccessMutationOnlyBlindByAdmin,
        }
      );
    }
  };

  const onDeleteOnlyCommentByAdmin = () => {
    if (reportData) {
      onUpdateBlindBoardCommentOnly(
        { boardCommentPid: reportData.targetId },
        {
          onError,
          onSuccess: onSuccessMutationOnlyBlindByAdmin,
        }
      );
    }
  };

  const onBlindPostImageByAdmin = () => {
    if (reportData && reportData.attachmentTargetId) {
      blindBoardPostAttachment(
        {
          boardPostAttachmentId: reportData.attachmentTargetId,
          blindType: AttachmentBlindType.Blinded,
        },
        {
          onError,
          onSuccess: () => onSuccessMutationByAdmin({ type: 'blindPostAttachment' }),
        }
      );
    }
  };

  const onShowDeleteModal = () => {
    setShowReportModal(false);

    showModal({
      type: MODAL_TYPE.ALERT,
      props: {
        title: `${reportType}을 삭제할까요?`,
        message: `삭제된 ${reportType}은 다시 복구할 수 없어요.`,
        buttons: [
          {
            type: MODAL_BUTTON_TYPE.DEFAULT,
            label: '취소',
            onClick: closeModal,
          },
          {
            type: MODAL_BUTTON_TYPE.DANGER,
            label: '삭제',
            onClick: isPost ? onDeletePost : onDeleteComment,
          },
        ],
      },
    });
  };

  const onShowImageBlindModalByAdmin = () => {
    setShowReportModal(false);

    showModal({
      type: MODAL_TYPE.ALERT,
      props: {
        title: '이미지를 숨김 처리할까요?',
        message: '커뮤니티 정책에 위반한 이미지가 맞는지 한 번 더 확인해주세요.',
        buttons: [
          {
            type: MODAL_BUTTON_TYPE.DEFAULT,
            label: '취소',
            onClick: closeModal,
          },
          {
            type: MODAL_BUTTON_TYPE.DANGER,
            label: '블라인드 처리',
            onClick: onBlindPostImageByAdmin,
          },
        ],
      },
    });
  };

  const onShowDeleteAndBannedModalByAdmin = () => {
    setShowReportModal(false);

    showModal({
      type: MODAL_TYPE.ALERT,
      props: {
        title: `${reportType} 삭제 및 이용 제한 할까요?`,
        message: `커뮤니티 정책에 위반한 ${reportType}이 맞는지 한 번 더 확인해 주세요. 자동으로 이용 제한도 걸리게 돼요.`,
        buttons: [
          {
            type: MODAL_BUTTON_TYPE.DEFAULT,
            label: '취소',
            onClick: closeModal,
          },
          {
            type: MODAL_BUTTON_TYPE.DANGER,
            label: '삭제',
            onClick: isPost ? onDeletePostAndBannedByAdmin : onDeleteCommentAndBannedByAdmin,
          },
        ],
      },
    });
  };

  const onShowOnlyDeleteModalByAdmin = () => {
    setShowReportModal(false);

    showModal({
      type: MODAL_TYPE.ALERT,
      props: {
        title: `해당 ${reportType}을 삭제할까요?`,
        message: `커뮤니티 정책에 위반한 ${reportType}이 맞는지 한 번 더 확인해 주세요.`,
        buttons: [
          {
            type: MODAL_BUTTON_TYPE.DEFAULT,
            label: '취소',
            onClick: closeModal,
          },
          {
            type: MODAL_BUTTON_TYPE.DANGER,
            label: '삭제',
            onClick: isPost ? onDeleteOnlyPostByAdmin : onDeleteOnlyCommentByAdmin,
          },
        ],
      },
    });
  };

  const generateAdminSelectList = () => {
    let selectItems: Array<SelectModalListItem> = [];
    if (isPost) {
      selectItems = [
        {
          Icon: () => (
            <IconFrame>
              <ContentFile size={16} bodyColor={theme.color.red500} edgeColor={theme.color.red300} />
            </IconFrame>
          ),
          text: '게시글 삭제',
          onClick: onShowOnlyDeleteModalByAdmin,
        },
        {
          Icon: () => (
            <IconFrame backgroundColor={theme.color.red200}>
              <UserBlockRemove />
            </IconFrame>
          ),
          text: '게시글 삭제 (이용 제한 포함)',
          onClick: onShowDeleteAndBannedModalByAdmin,
        },
      ];
      if (reportData.attachmentTargetId) {
        selectItems = [
          {
            Icon: () => (
              <IconFrame backgroundColor={theme.color.gray100}>
                <Landscape />
              </IconFrame>
            ),
            text: '이미지 블라인드',
            onClick: onShowImageBlindModalByAdmin,
          },
          ...selectItems,
        ];
      }
    }
    if (!isPost) {
      selectItems = [
        {
          Icon: () => (
            <IconFrame>
              <MailChatBubbleTypingOval color={theme.color.red500} />
            </IconFrame>
          ),
          text: '댓글 삭제',
          onClick: onShowOnlyDeleteModalByAdmin,
        },
        {
          Icon: () => (
            <IconFrame backgroundColor={theme.color.red200}>
              <UserBlockRemove />
            </IconFrame>
          ),
          text: '댓글 삭제 (이용 제한 포함)',
          onClick: onShowDeleteAndBannedModalByAdmin,
        },
      ];
    }

    setReportItems(selectItems);
  };

  const generateSelectList = () => {
    if (reportData?.isAdmin) {
      return generateAdminSelectList();
    }
    let selectItems: Array<SelectModalListItem> = [
      {
        Icon: () => <QnaReport />,
        text: isPost ? '게시글 신고' : '댓글 신고',
        onClick: () => onReportPost(false),
      },
      {
        Icon: () => <UserReport />,
        text: '사용자 신고',
        onClick: () => onReportPost(true),
      },
    ];
    if (reportData?.isMine) {
      selectItems = [
        {
          Icon: () => <UserBlock />,
          text: '삭제하기',
          onClick: onShowDeleteModal,
        },
      ];
    }

    setReportItems(selectItems);
  };

  useEffect(() => {
    if (reportItems?.length) {
      return setShowReportModal(true);
    }
    setShowReportModal(false);
  }, [reportItems]);

  useEffect(() => {
    if (reportData) {
      generateSelectList();
    }
  }, [reportData]);

  useEffect(() => {
    if (!focused) {
      onCloseReportModal();
    }
  }, [focused]);

  return {
    showReportModal,
    onShowReportModal,
    onCloseReportModal,
    reportData,
    reportItems,
  };
};

export default usePostReportModal;
